import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext.tsx";
import MessageProvider from "./components/Message/MessageProvider.tsx";
import ProtectedRoute from "./components/ProtectedRoute.tsx";
import DashboardLayout from "./pages/DashboardLayout.tsx";
import DashboardPage from "./pages/Dashboard.tsx";
import DeviceHealthAlertPage from "./pages/DeviceHealthAlert.tsx";
import DiscoverPage from "./pages/Discover.tsx";
import OrganizationPage from "./pages/Organization.tsx";
import OrganizationDetailPage from "./pages/OrganizationDetail.tsx";
import SiteHealthAlertRulesPage from "./pages/SiteHealthAlertRules.tsx"; // Add import
import UserSettingsPage from "./pages/UserSettings.tsx";
import UserProfile from "./pages/UserProfile.tsx";
import Login from "./pages/Login.tsx";
import PartnerSetup from "./pages/PartnerSetup.tsx";
import { useAuth } from "./contexts/AuthContext.tsx";

// 创建基于角色的路由组件
const RoleBasedRoute = ({
  children,
  allowedRoles,
  fallback = <Navigate to="/dashboard" replace />,
}: {
  children: React.ReactNode;
  allowedRoles: string[];
  fallback?: React.ReactNode;
}) => {
  const { user } = useAuth();

  if (!user || !allowedRoles.includes(user.role?.name)) {
    return fallback;
  }

  return <>{children}</>;
};

function App() {
  return (
    <MessageProvider>
      <AuthProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/partner/setup" element={<PartnerSetup />} />
            <Route path="/partner/setup-password" element={<PartnerSetup />} />

            {/* Protected Routes */}
            <Route path="/" element={<ProtectedRoute />}>
              <Route path="/" element={<DashboardLayout />}>
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<DashboardPage />} />
                <Route path="alerts" element={<DeviceHealthAlertPage />} />
                <Route path="discover" element={<DiscoverPage />} />
                <Route
                  path="organization"
                  element={
                    <RoleBasedRoute
                      allowedRoles={["Partner Admin", "Partner Tech"]}
                    >
                      <OrganizationPage />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="organization/:orgId"
                  element={
                    <RoleBasedRoute
                      allowedRoles={["Partner Admin", "Partner Tech"]}
                    >
                      <OrganizationDetailPage />
                    </RoleBasedRoute>
                  }
                />

                {/* Settings routes - Hidden for Partner Viewer */}
                <Route
                  path="settings/users"
                  element={
                    <RoleBasedRoute allowedRoles={["Partner Admin"]}>
                      <UserSettingsPage />
                    </RoleBasedRoute>
                  }
                />
                <Route
                  path="settings/alert-rule"
                  element={
                    <RoleBasedRoute
                      allowedRoles={["Partner Admin", "Partner Tech"]}
                    >
                      <SiteHealthAlertRulesPage />
                    </RoleBasedRoute>
                  }
                />

                <Route path="profile" element={<UserProfile />} />
              </Route>
            </Route>

            {/* Fallback Route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </BrowserRouter>
      </AuthProvider>
    </MessageProvider>
  );
}

export default App;
